import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { CONTROL_SYSTEM_LABEL, controlSystemDisplayName, deviceDisplayName, linkedDeviceDisplayName, locationDisplayName, locationRefName } from '@/cloud/services/device-display';
import { LocationDto } from '@/cloud/models/tree.models';
import { effectiveCapacity, isRoomType, roomAvailabilityLine, roomStatus, summarizeRoomUsage } from '@/cloud/models/room-usage.models';
import { DEFAULT_ROOM_FILTERS, ROOM_STATUS_GROUPS, RoomFilters, RoomListRow, RoomStatusFilter, filterRooms, hasActiveRoomFilters, matchesRoomCapacity, matchesRoomType, roomCapacityChoices, roomStatusChoices, roomTypeChoices } from '@/cloud/models/room-filters';
import { RoomCardComponent } from '@/cloud/components/room-usage/room-card.component';
import { RoomUsageNowComponent } from '@/cloud/components/room-usage/room-usage-now.component';
import { RoomFeedbackHighlightComponent } from '@/cloud/components/room-usage/room-feedback-highlight.component';
import { RoomRatingComponent } from '@/cloud/components/room-usage/room-rating.component';
import { CloudFeedbackService } from '@/cloud/insights/cloud-feedback.service';
import { ROOM_FEEDBACK_DAYS, RoomFeedbackStats, summarizeRoomFeedback } from '@/cloud/models/feedback.models';
import { ROOM_STATUS_ICON, ROOM_STATUS_TONE, RoomStatusPillComponent, availabilityText } from '@/cloud/components/room-usage/room-status-pill.component';
import { LocationSettingsComponent } from '@/cloud/components/location-settings/location-settings.component';
import { RoomProfileDialogComponent, RoomProfileTarget } from '@/cloud/components/room-usage/room-profile-dialog.component';
import { translateCount } from '@/layout/service/translate-count';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, Component, DestroyRef, Injector, LOCALE_ID, ProviderToken, computed, inject, signal, effect, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { asyncScheduler, filter, throttleTime } from 'rxjs';
import moment from 'moment';
import { LocationTreeComponent } from '@/cloud/components/location-tree/location-tree.component';
import { LocationPickerComponent } from '@/cloud/components/location-picker/location-picker.component';
import { HotlistComponent } from '@/cloud/components/hotlist/hotlist.component';
import { DeviceMapComponent } from '@/cloud/components/device-map/device-map.component';
import { NamespacePackagesComponent } from '@/cloud/components/namespace-packages/namespace-packages.component';
import { LocationDetailsComponent } from '@/cloud/components/location-details/location-details.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { BREADCRUMB_HOME, BreadcrumbsComponent } from '@/shared/breadcrumbs/breadcrumbs.component';
import { ButtonModule } from 'primeng/button';
import { TreeNode } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ContextMenuModule } from 'primeng/contextmenu';
import { MenuItem } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { TabsModule } from 'primeng/tabs';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { StatusBadgeComponent } from '@/shared/ui/status-badge/status-badge.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { SectionHeaderComponent } from '@/shared/ui/section-header/section-header.component';
import { compareStatus, StatusDescriptor, StatusKey } from '@/shared/ui/status.model';
import { deriveDeviceStatus, deriveLinkedDeviceStatus } from '@/cloud/models/device-status';
import { TenantService } from '@/cloud/services/tenant.service';
import { TranslationService } from '@/layout/service/translation.service';
import { LayoutService } from '@/layout/service/layout.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbModule,
    BreadcrumbsComponent,
    RouterModule,
    TableModule,
    TagModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    FormsModule,
    LocationTreeComponent,
    LocationPickerComponent,
    ContextMenuModule,
    HotlistComponent,
    DeviceMapComponent,
    NamespacePackagesComponent,
    LocationDetailsComponent,
    MultiSelectModule,
    StatusBadgeComponent,
    EmptyStateComponent,
    LoadingSkeletonComponent,
    SectionHeaderComponent,
    TabsModule,
    SelectModule,
    SelectButtonModule,
    RoomCardComponent,
    RoomUsageNowComponent,
    RoomFeedbackHighlightComponent,
    RoomRatingComponent,
    RoomStatusPillComponent,
    RoomProfileDialogComponent,
    LocationSettingsComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Dashboard {
  /** Template helper — see deviceDisplayName for the precedence. */
  displayName = deviceDisplayName;

  private api = inject(CloudApiService);
  private tenants = inject(TenantService);
  readonly t = inject(TranslationService).t;
  public route = inject(ActivatedRoute);
  router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private readonly requestedTab = signal<string | null>(null);
  readonly activeTab = computed<'overview' | 'rooms' | 'systems' | 'devices' | 'settings'>(() => {
    const tab = this.requestedTab();
    if (tab === 'settings') return this.locationId() ? 'settings' : 'overview';
    return tab === 'rooms' || tab === 'systems' || tab === 'devices' ? tab : 'overview';
  });
  readonly roomSearch = signal('');

  changeTab(tab: string | number | undefined): void {
    if (tab !== 'overview' && tab !== 'rooms' && tab !== 'systems' && tab !== 'devices' && tab !== 'settings') return;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === 'overview' ? null : tab },
      queryParamsHandling: 'merge'
    });
  }

  openSystems(filter: 'all' | 'online' | 'attention' | 'setup'): void {
    this.systemFilter.set(filter);
    this.changeTab('systems');
  }

  /** Coarse clock so "Active but silent for 5 minutes" flips to Offline without a reload. */
  private now = signal(Date.now());

  /** Visual status for a System row. One derivation, shared with the room view and badges. */
  deviceStatus(device: any): StatusDescriptor {
    return deriveDeviceStatus(device, this.now());
  }

  linkedStatus = deriveLinkedDeviceStatus;

  /** True while a device still needs its one-time registration code (pending or expired). */
  isAwaitingRegistration(device: any): boolean {
    const key = deriveDeviceStatus(device, this.now()).key;
    return key === 'pending' || key === 'expired';
  }

  // ---- Systems table: optional columns, chosen by the user and remembered per browser
  readonly systemsColumns: { field: string; header: string }[] = [
    { field: 'lastCheckInUtc', header: 'Last Check-In' },
    { field: 'model', header: 'Model' },
    { field: 'firmwareVersion', header: 'Firmware' },
    { field: 'version', header: 'Package Version' },
    { field: 'uptimeSeconds', header: 'Uptime' },
    { field: 'ipAddress', header: 'IP Address' },
    { field: 'hostName', header: 'Hostname' },
    { field: 'packageNamespace', header: 'Package' },
    { field: 'processorId', header: 'ID' },
    { field: 'createdUtc', header: 'Added' }
  ];
  private static readonly SYSTEMS_COLUMNS_KEY = 'dashboard.systemsColumns';
  private static readonly DEFAULT_SYSTEMS_COLUMNS = ['lastCheckInUtc', 'model', 'firmwareVersion', 'version', 'uptimeSeconds'];
  visibleSystemsColumns = signal<string[]>(Dashboard.loadVisibleColumns());
  selectedSystemsColumns = computed(() => {
    const visible = this.visibleSystemsColumns();
    return this.systemsColumns.filter((c) => visible.includes(c.field));
  });

  private static loadVisibleColumns(): string[] {
    try {
      const raw = localStorage.getItem(Dashboard.SYSTEMS_COLUMNS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) return parsed;
    } catch {
      /* fall through to defaults */
    }
    return [...Dashboard.DEFAULT_SYSTEMS_COLUMNS];
  }

  onSystemsColumnsChange(selected: { field: string }[] | null) {
    // Keep the declared column order regardless of the order the user ticked them in.
    const chosen = new Set((selected ?? []).map((c) => c.field));
    const ordered = this.systemsColumns.map((c) => c.field).filter((f) => chosen.has(f));
    this.visibleSystemsColumns.set(ordered);
    try {
      localStorage.setItem(Dashboard.SYSTEMS_COLUMNS_KEY, JSON.stringify(ordered));
    } catch {
      /* ignore */
    }
  }

  /** Cell text for an optional Systems column. */
  systemCell(device: any, field: string): string {
    switch (field) {
      case 'lastCheckInUtc':
        return this.lastCheckInAgo(device);
      case 'createdUtc':
        return this.formatCreatedDate(device);
      case 'uptimeSeconds':
        return this.formatUptime(device);
      default: {
        const v = device?.[field];
        return v === null || v === undefined || v === '' ? '\u2014' : String(v);
      }
    }
  }

  // expose signals from service
  public devices = this.api.devices;
  readonly systemsLoading = computed(() => this.devices() === null || this.api.devicesLoading());
  readonly systemsError = this.api.devicesError;

  public namespaces = this.api.namespaces;
  public locations = this.api.locations;
  // currently selected tree node from child component
  selectedNode = signal<TreeNode | null>(null);

  // Generate unique state keys for tables based on current namespace/location
  appInstancesTableStateKey = computed(() => {
    const nsId = this.namespaceId();
    const locId = this.locationId();
    return nsId ? `appInstances_${nsId}_${locId || 'root'}` : 'appInstances_default';
  });

  linkedDevicesTableStateKey = computed(() => {
    const nsId = this.namespaceId();
    const locId = this.locationId();
    return nsId ? `linkedDevices_${nsId}_${locId || 'root'}` : 'linkedDevices_default';
  });

  // reactive route params
  namespaceId = signal<string | null>(null);
  locationId = signal<string | null>(null);
  private selectedLocation = computed(() => this.locations()?.find((location) => location.id === this.locationId() && location.appNamespaceId === this.namespaceId()));
  selectedRoomId = computed(() => (this.selectedLocation()?.type === 'room' ? this.locationId() : null));
  selectedLocationName = computed(() => this.selectedLocation()?.name ?? '');

  readonly navigationLoading = signal(true);
  private minimumHoldElapsed = signal(false);
  private navigationTimer?: ReturnType<typeof setTimeout>;
  private navigationScope: string | null = null;
  private settledRoomId = signal<string | null>(null);

  readonly locationUnavailable = computed(() => {
    const ns = this.namespaceId();
    if (!ns || !this.locationId() || this.selectedLocation()) return false;
    const state = this.api.locationLoadStates()[ns];
    return state === 'loaded' || state === 'error';
  });

  private scopePending = computed(() => {
    if (!this.namespaceId()) return false;
    if (this.locationUnavailable()) return false;
    if (this.locationId() && !this.selectedLocation()) return true;
    const roomId = this.selectedRoomId();
    if (roomId) return this.settledRoomId() !== roomId;
    return (!this.systemsError() && this.systemsLoading()) || this.roomsLoading() || this.linkedDevicesLoading() || this.loadedLinkedScope() !== this.scopeKey(this.namespaceId(), this.locationId());
  });

  private finishNavigation = effect(() => {
    if (this.navigationLoading() && this.minimumHoldElapsed() && !this.scopePending()) {
      this.navigationLoading.set(false);
    }
  });

  onRoomLoadState(state: { locationId: string; loading: boolean }) {
    if (state.locationId === this.selectedRoomId()) {
      this.settledRoomId.set(state.loading ? null : state.locationId);
    }
  }

  // Computed signals cache until their inputs change, including the location hierarchy.
  private locationParentChain = computed(() => {
    const nsId = this.namespaceId();
    const locId = this.locationId();
    if (!nsId || !locId) return null;

    const locations = this.locations();
    const locsInNs = (locations || []).filter((l) => l.appNamespaceId === nsId);
    const map = new Map<string, (typeof locsInNs)[0]>();
    locsInNs.forEach((l) => map.set(l.id, l));

    const chain: (typeof locsInNs)[0][] = [];
    let cursor = map.get(locId);
    const visited = new Set<string>();
    while (cursor && !visited.has(cursor.id)) {
      visited.add(cursor.id);
      chain.unshift(cursor);
      cursor = cursor.parentId ? map.get(cursor.parentId) : undefined;
    }

    return chain;
  });

  // Reliable breadcrumb derived directly from route params + location parent chain (not relying on TreeNode.parent linkage)
  breadcrumbItems = computed(() => {
    const nsId = this.namespaceId();
    if (!nsId) return [] as { label: string; routerLink: any[] | string }[];
    const namespaces = this.namespaces();
    const ns = namespaces?.find((n) => n.id === nsId);
    if (!ns) return [];

    const items: { label: string; routerLink: any[] | string }[] = [];
    // namespace root
    items.push({ label: ns.name, routerLink: ['/dashboard', nsId] });

    const chain = this.locationParentChain();
    if (chain) {
      chain.forEach((c) => items.push({ label: c.name, routerLink: ['/dashboard', nsId, c.id] }));
    }

    return items;
  });
  breadcrumbHome = BREADCRUMB_HOME;

  // Regenerate code dialog state
  showRegenerateDialog = signal(false);
  regenTarget = signal<any | null>(null);
  regenSubmitting = signal(false);
  regenError = signal<string | null>(null);
  regenResult = signal<{ id: string; registrationCode: string; registrationExpiresUtc?: string | null } | null>(null);
  regenDeleteSubmitting = signal(false);
  regenDeleteError = signal<string | null>(null);

  // Device context menu & move dialog state
  // Rename dialog state
  showRenameDialog = signal(false);
  renameTarget = signal<any | null>(null);
  renameValue = signal('');
  renameError = signal<string | null>(null);
  renameSubmitting = signal(false);

  deviceContextMenuItems: MenuItem[] = [];
  linkedDeviceContextMenuItems: MenuItem[] = [];
  contextMenuTarget = signal<any | null>(null);
  showMoveDialog = signal(false);
  moveSubmitting = signal(false);
  moveError = signal<string | null>(null);
  moveLocationNodeId = signal<string | null>(null); // chosen new location id
  // Location options for move/select (include root option)
  moveLocationOptions = computed(() => {
    const nsId = this.namespaceId();
    if (!nsId) return [] as { label: string; value: string }[];
    const locs = (this.locations() || []).filter((l) => l.appNamespaceId === nsId);
    // Build parent -> children map
    const byParent = new Map<string | null, any[]>();
    locs.forEach((l) => {
      const key = l.parentId || null;
      const arr = byParent.get(key) || [];
      arr.push(l);
      byParent.set(key, arr);
    });
    // Sort children arrays by name for stable ordering
    for (const arr of byParent.values()) arr.sort((a, b) => linkedDeviceDisplayName(a).localeCompare(linkedDeviceDisplayName(b)));
    const result: { label: string; value: string }[] = [{ label: '(Root / None)', value: '' }];
    const walk = (parentId: string | null, depth: number) => {
      const children = byParent.get(parentId);
      if (!children) return;
      for (const child of children) {
        const prefix = depth === 0 ? '' : '— '.repeat(depth); // em dash indentation
        result.push({ label: prefix + child.name, value: child.id });
        walk(child.id, depth + 1);
      }
    };
    walk(null, 0);
    return result;
  });

  // Delete device dialog state (for unclaimed devices)
  showDeleteDeviceDialog = signal(false);
  deleteDeviceSubmitting = signal(false);
  deleteDeviceError = signal<string | null>(null);
  deleteDeviceTarget = signal<any | null>(null);

  // Recalculate when locations load or move, but not on device telemetry updates.
  private locationDescendants = computed(() => {
    const nsId = this.namespaceId();
    const locId = this.locationId();
    if (!nsId || !locId) return null;

    // Build descendants set for selected location
    const locs = (this.locations() || []).filter((l) => l.appNamespaceId === nsId);
    const byParent = new Map<string | null, string[]>();
    locs.forEach((l) => {
      const key = l.parentId || null;
      const arr = byParent.get(key) || [];
      arr.push(l.id);
      byParent.set(key, arr);
    });

    const descendants = new Set<string>();
    const stack: string[] = [locId];
    while (stack.length) {
      const current = stack.pop()!;
      if (descendants.has(current)) continue;
      descendants.add(current);
      const children = byParent.get(current) || [];
      children.forEach((c) => stack.push(c));
    }

    return descendants;
  });

  readonly roomsLoading = computed(() => {
    const ns = this.namespaceId();
    const state = ns ? this.api.locationLoadStates()[ns] : undefined;
    return !!ns && state !== 'error' && (this.locations() === null || !state || state === 'loading');
  });
  readonly roomsError = computed(() => {
    const ns = this.namespaceId();
    return !!ns && this.api.locationLoadStates()[ns] === 'error';
  });
  readonly scopedRooms = computed<RoomListRow[]>(() => {
    const namespaceId = this.namespaceId();
    const locations: LocationDto[] = (this.locations() ?? []).filter((location) => location.appNamespaceId === namespaceId);
    const byId = new Map(locations.map((location) => [location.id, location]));
    const systems = new Map((this.devices() ?? []).filter((device) => device.appNamespaceId === namespaceId).map((device) => [device.id, device]));
    const equipmentCounts = new Map<string, number>();
    for (const device of this.filteredLinkedDevices()) {
      const id: string | undefined = device.locationNodeId;
      if (id) equipmentCounts.set(id, (equipmentCounts.get(id) ?? 0) + 1);
    }
    const descendants = this.locationDescendants();
    const loaded = this.roomFeedback();
    const feedbackByRoom = loaded?.namespaceId === namespaceId ? loaded.rooms : null;
    return locations
      .filter((location) => location.type === 'room' && (!this.locationId() || descendants?.has(location.id)))
      .map((room) => {
        const parent = byId.get(room.parentId ?? '');
        const owner = systems.get(room.controlSystemDeviceId ?? '');
        const status = owner ? deriveDeviceStatus(owner, this.now()) : null;
        const usage = room.usage ?? null;
        const feedback = feedbackByRoom?.get(room.id) ?? null;
        return {
          ...room,
          displayName: locationDisplayName(room.name, room.type),
          parentName: parent ? locationDisplayName(parent.name, parent.type) : this.namespaces()?.find((namespace) => namespace.id === namespaceId)?.name,
          systemName: owner ? deviceDisplayName(owner) : null,
          status,
          deviceCount: this.linkedDevicesLoading() || this.linkedDevicesError() ? null : (equipmentCounts.get(room.id) ?? 0),
          usage,
          // Usage from a room whose control system is not online is the last reported state, not live.
          usageStale: !!usage && status?.key !== 'online',
          roomType: isRoomType(room.roomType) ? room.roomType : null,
          cloudCapacity: typeof room.capacity === 'number' ? room.capacity : null,
          capacity: effectiveCapacity(room),
          roomStatus: roomStatus(usage),
          feedback,
          feedbackAverage: feedback?.average ?? null
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  });
  /** Live occupancy / booking counts for the Overview "Room usage now" section. */
  readonly roomUsage = computed(() => summarizeRoomUsage(this.scopedRooms()));

  // ---- Room feedback: average rating per room over the last 30 days, for the rooms list and the Overview highlight
  private injector = inject(Injector);
  /** The tenant's per-room feedback; null until loaded, or when feedback is unavailable (not shown then). */
  private readonly roomFeedback = signal<{ namespaceId: string; rooms: Map<string, RoomFeedbackStats> } | null>(null);
  private roomFeedbackRequest = 0;
  /** Poorly and well rated rooms in scope; null while no room in scope has feedback, so nothing is shown. */
  readonly feedbackHighlight = computed(() => {
    const summary = summarizeRoomFeedback(this.scopedRooms());
    return summary.ratedRooms > 0 ? summary : null;
  });
  /** Show the rooms table's Rating column: some room in scope has feedback. */
  readonly roomsHaveFeedback = computed(() => this.feedbackHighlight() !== null);

  private async loadRoomFeedback(namespaceId: string): Promise<void> {
    const request = ++this.roomFeedbackRequest;
    const service = this.resolveService(CloudFeedbackService);
    if (!service) return;
    const to = new Date();
    try {
      const summary = await service.getRoomSummaries(namespaceId, new Date(to.getTime() - ROOM_FEEDBACK_DAYS * 86_400_000), to);
      if (request !== this.roomFeedbackRequest) return;
      this.roomFeedback.set({ namespaceId, rooms: new Map((summary.rooms ?? []).map((room) => [room.locationId, room])) });
    } catch {
      // Feedback is supplementary: without it the rooms list and Overview just leave it out.
      if (request === this.roomFeedbackRequest && this.roomFeedback()?.namespaceId !== namespaceId) this.roomFeedback.set(null);
    }
  }

  /** The feedback service needs HTTP; a host without it (an API stub in tests) just shows no feedback. */
  private resolveService<T>(token: ProviderToken<T>): T | null {
    try {
      return this.injector.get(token, null);
    } catch {
      return null;
    }
  }

  // ---- Rooms list: cards or a table, filtered by status, room type and capacity
  private static readonly ROOMS_VIEW_KEY = 'dashboard.roomsView';
  readonly roomsView = signal<'cards' | 'list'>(Dashboard.loadRoomsView());
  readonly roomFilters = signal<RoomFilters>({ ...DEFAULT_ROOM_FILTERS });
  readonly roomFiltersActive = computed(() => hasActiveRoomFilters(this.roomFilters()));
  readonly roomViewOptions = computed(() => [
    { value: 'cards', label: this.t()('rooms.view.cards'), icon: 'pi pi-th-large' },
    { value: 'list', label: this.t()('rooms.view.list'), icon: 'pi pi-list' }
  ]);
  /** Rooms in scope that match the search box. */
  private readonly searchedRooms = computed(() => {
    const query = this.roomSearch().trim().toLowerCase();
    return this.scopedRooms().filter((room) => !query || [room.displayName, room.code, room.parentName, room.systemName].some((value) => value?.toLowerCase().includes(query)));
  });
  /** Status chips with live counts over the rooms the search, type and capacity filters leave; empty groups are left out. */
  readonly roomStatusChips = computed(() => {
    const { status, type, minCapacity } = this.roomFilters();
    const rooms = this.searchedRooms().filter((room) => matchesRoomType(room.roomType, type) && matchesRoomCapacity(room.capacity, minCapacity));
    const chip = (value: RoomStatusFilter, label: string, count: number, icon: string | null, tone: string) => ({ value, label, count, icon, tone, selected: status === value });
    return [
      chip('all', this.t()('rooms.filter.status.all'), rooms.length, null, 'neutral'),
      ...roomStatusChoices(rooms, status).map(({ group, count }) => {
        const lead = ROOM_STATUS_GROUPS[group][0];
        return chip(group, this.t()('rooms.filter.status.' + group), count, ROOM_STATUS_ICON[lead], ROOM_STATUS_TONE[lead]);
      })
    ];
  });
  /** Room types the rooms in scope have; empty (filter hidden) while no room has a type. */
  readonly roomTypeOptions = computed(() => {
    const choices = roomTypeChoices(this.scopedRooms(), this.roomFilters().type);
    if (!choices.length) return [];
    return [{ value: 'all', label: this.t()('rooms.filter.type.all') }, ...choices.map((value) => ({ value, label: this.t()('roomType.' + value) }))];
  });
  /** Seat thresholds some room in scope meets; empty (filter hidden) while no capacity is known. */
  readonly roomCapacityOptions = computed(() => {
    const choices = roomCapacityChoices(this.scopedRooms(), this.roomFilters().minCapacity);
    if (!choices.length) return [];
    return [0, ...choices].map((value) => ({ value, label: value === 0 ? this.t()('rooms.filter.capacity.any') : translateCount(this.t(), 'rooms.filter.capacity.min', value) }));
  });

  private static loadRoomsView(): 'cards' | 'list' {
    try {
      return localStorage.getItem(Dashboard.ROOMS_VIEW_KEY) === 'list' ? 'list' : 'cards';
    } catch {
      return 'cards';
    }
  }

  setRoomsView(view: unknown): void {
    if (view !== 'cards' && view !== 'list') return;
    this.roomsView.set(view);
    try {
      localStorage.setItem(Dashboard.ROOMS_VIEW_KEY, view);
    } catch {
      /* per-viewer convenience only */
    }
  }

  setRoomFilter<K extends keyof RoomFilters>(key: K, value: RoomFilters[K] | null): void {
    this.roomFilters.update((filters) => ({ ...filters, [key]: value ?? DEFAULT_ROOM_FILTERS[key] }));
  }

  /** Select a status chip; selecting the chip already selected shows every status again. */
  toggleRoomStatus(status: RoomStatusFilter): void {
    this.setRoomFilter('status', this.roomFilters().status === status ? 'all' : status);
  }

  clearRoomFilters(): void {
    this.roomSearch.set('');
    this.roomFilters.set({ ...DEFAULT_ROOM_FILTERS });
  }

  /** Translate a string with a `{n}` placeholder, using its singular form for 1. */
  tn(key: string, n: number | null): string {
    return translateCount(this.t(), key, n);
  }

  readonly visibleRooms = computed(() => filterRooms(this.searchedRooms(), this.roomFilters()));
  readonly roomsShowing = computed(() => translateCount(this.t(), 'rooms.showing', this.scopedRooms().length, { x: this.visibleRooms().length }));

  private locale = inject(LOCALE_ID);
  /** The Meeting cell of the rooms table: the same calendar line the cards show. */
  roomMeetingText(room: RoomListRow): string {
    return availabilityText(roomAvailabilityLine(room.usage), this.t(), (iso) => formatDate(iso, 'shortTime', this.locale)) ?? '\u2014';
  }

  /** People / capacity cell: "3 / 8", "3", "8 seats" or "Capacity not set". */
  roomPeopleText(room: RoomListRow): string {
    const people = room.usage?.peopleCount;
    if (typeof people === 'number') return room.capacity !== null ? `${people} / ${room.capacity}` : String(people);
    return room.capacity !== null ? translateCount(this.t(), 'roomCapacity.seats', room.capacity) : this.t()('roomCapacity.notSet');
  }

  // ---- Room profile (capacity and room type)
  readonly canManageLocations = computed(() => this.api.hasCapability?.(this.namespaceId(), 'locations.manage') ?? false);
  readonly roomProfileVisible = signal(false);
  readonly roomProfileTarget = signal<RoomProfileTarget | null>(null);

  editRoomProfile(room: RoomListRow): void {
    this.roomProfileTarget.set({ id: room.id, name: room.displayName, capacity: room.cloudCapacity, roomType: room.roomType, sensorCapacity: room.usage?.capacity ?? null });
    this.roomProfileVisible.set(true);
  }

  // Filter devices by current selection including inheritance (selected location + all descendants), sorted by name
  filteredDevices = computed(() => {
    const all = this.devices();
    if (!all) return [];
    const nsId = this.namespaceId();
    if (!nsId) return [];
    const locId = this.locationId();

    // Filter by namespace or location
    const descendants = this.locationDescendants();
    const base = !locId ? all.filter((d) => d.appNamespaceId === nsId) : descendants ? all.filter((d) => d.appNamespaceId === nsId && d.locationNodeId && descendants.has(d.locationNodeId)) : [];

    // Order by name, not status/check-in. Those change on every live snapshot, so a list
    // sorted by them reshuffles constantly while you're reading it (#258). The id tie-break
    // keeps systems with the same name in a fixed order. Status order is still one click away.
    const now = this.now();
    const sorted = [...base].sort(
      (a, b) => this.displayName(a).localeCompare(this.displayName(b), undefined, { sensitivity: 'base', numeric: true }) || String(a.id).localeCompare(String(b.id))
    );

    // p-table's built-in column sort compares raw field values, so the "Name" column needs a
    // field that matches what's actually rendered (deviceDisplayName's resolved fallback chain,
    // not the raw — often empty — registration `name`), or clicking the header reorders rows
    // by a value nobody sees.
    // Derive each row's status once here. Every consumer below (the summary counts, the
    // status chips, the table badge) reads these fields instead of re-deriving, which used
    // to re-parse dates and allocate a descriptor per device per consumer on every 30s tick.
    return sorted.map((d) => {
      const sortName = this.displayName(d);
      const status = deriveDeviceStatus(d, now);
      return {
        ...d,
        sortName,
        status,
        statusKey: status.key,
        statusOrder: status.sortOrder,
        searchIndex: [sortName, d.id, d.model, d.ipAddress]
          .map((v) => String(v ?? ''))
          .join(' ')
          .toLowerCase()
      };
    });
  });

  /**
   * The overview tiles, the summary counts and the table filter are all driven from this one
   * table, so a new status bucket is added in a single place rather than in the counts, the
   * filter predicate and the result label separately.
   */
  readonly systemFilters = [
    { key: 'all', label: 'All systems', icon: 'pi pi-server', hint: 'In this location and below', match: () => true },
    { key: 'online', label: 'Online', icon: 'pi pi-check-circle metric-online', hint: 'Reporting to the cloud', match: (k: StatusKey) => k === 'online' },
    // Icons come from the same vocabulary as StatusBadge in the table below, so one fleet
    // state is not drawn two different ways on the same page.
    { key: 'attention', label: 'Review status', icon: 'pi pi-exclamation-triangle metric-warning', hint: 'Offline, errors or unavailable', match: (k: StatusKey) => k === 'offline' || k === 'warning' || k === 'missing' || k === 'unknown' },
    { key: 'setup', label: 'Awaiting setup', icon: 'pi pi-clock', hint: 'Pending adoption or expired code', match: (k: StatusKey) => k === 'pending' || k === 'expired' }
  ] as const;

  systemSearch = signal('');
  systemFilter = signal<'all' | 'online' | 'attention' | 'setup'>('all');
  locationsExpanded = signal(false);
  private readonly layout = inject(LayoutService);
  /** Pinned column: hidden or shown with the breadcrumb toggle. */
  readonly locationsHidden = this.layout.locationSidebarCollapsed;
  readonly locationsPinned = this.layout.locationSidebarPinned;
  /** Unpinned: the navigator slides over the page while this is set, and closes once a location is picked. */
  readonly locationsFloatingOpen = signal(false);
  readonly locationsShown = computed(() => (this.locationsPinned() ? !this.locationsHidden() : this.locationsFloatingOpen()));
  private readonly closeFloatingOnNavigate = effect(() => {
    this.namespaceId();
    this.locationId();
    untracked(() => this.locationsFloatingOpen.set(false));
  });

  toggleLocations(): void {
    if (this.locationsPinned()) this.locationsHidden.set(!this.locationsHidden());
    else this.locationsFloatingOpen.set(!this.locationsFloatingOpen());
  }

  closeLocations(): void {
    if (this.locationsPinned()) this.locationsHidden.set(true);
    else this.locationsFloatingOpen.set(false);
  }

  /** Pinning shows the column; unpinning tucks it away until the toggle opens it over the page. */
  toggleLocationsPinned(): void {
    const pin = !this.locationsPinned();
    this.layout.setLocationSidebarPinned(pin);
    this.locationsFloatingOpen.set(false);
    if (pin) this.locationsHidden.set(false);
  }
  scopeName = computed(() => {
    const location = this.locations()?.find((item) => item.id === this.locationId());
    const namespace = this.namespaces()?.find((item) => item.id === this.namespaceId());
    return location?.name || namespace?.name || 'Fleet overview';
  });
  /** Counts for every tile in one pass over the rows. */
  systemSummary = computed(() => {
    const totals: Record<string, number> = {};
    for (const filter of this.systemFilters) totals[filter.key] = 0;
    for (const device of this.filteredDevices()) {
      for (const filter of this.systemFilters) {
        if (filter.match(device.statusKey)) totals[filter.key]++;
      }
    }
    return totals;
  });
  selectedFilter = computed(() => this.systemFilters.find((f) => f.key === this.systemFilter()) ?? this.systemFilters[0]);
  visibleSystems = computed(() => {
    const query = this.systemSearch().trim().toLowerCase();
    const matchesStatus = this.selectedFilter().match;
    return this.filteredDevices().filter((device) => matchesStatus(device.statusKey) && (!query || device.searchIndex.includes(query)));
  });

  clearSystemFilters() {
    this.systemSearch.set('');
    this.systemFilter.set('all');
  }

  // Filter linked devices similarly
  filteredLinkedDevices = signal<any[]>([]);
  linkedDevicesLoading = signal(false);
  readonly linkedDevicesError = signal<string | null>(null);
  private readonly loadedLinkedScope = signal<string | null>(null);
  private linkedDevicesRequest = 0;
  private scopeKey(namespaceId: string | null, locationId: string | null | undefined): string {
    return JSON.stringify([namespaceId, locationId || null]);
  }

  readonly emptyLocation = computed(() => {
    const nsId = this.namespaceId();
    const locId = this.locationId();
    const location = this.locations()?.find((location) => location.id === locId && location.appNamespaceId === nsId);
    const scopeReady = locId ? !!location && location.type !== 'room' : !!this.namespaces()?.some((namespace) => namespace.id === nsId);
    return (
      !!nsId &&
      scopeReady &&
      !this.selectedRoomId() &&
      !this.systemsLoading() &&
      !this.systemsError() &&
      !this.roomsLoading() &&
      !this.roomsError() &&
      this.scopedRooms().length === 0 &&
      !this.linkedDevicesLoading() &&
      !this.linkedDevicesError() &&
      this.loadedLinkedScope() === this.scopeKey(nsId, locId) &&
      this.filteredDevices().length === 0 &&
      this.filteredLinkedDevices().length === 0
    );
  });

  // /dashboard without a tenant: go to the selected tenant (TenantService restores the stored choice
  // or falls back to the first tenant once the list has loaded).
  private tenantRedirectEffect = effect(() => {
    const nsId = this.namespaceId();
    const selected = this.tenants.selectedTenantId();
    if (!nsId && selected) {
      this.router.navigate(['/dashboard', selected], { replaceUrl: true });
    }
  });

  constructor() {
    effect(() => {
      if (this.requestedTab() === 'documents' && this.namespaceId() && !this.locationId()) {
        void this.router.navigate(['/files', this.namespaceId()], { replaceUrl: true });
      }
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.requestedTab.set(params.get('tab'));
    });
    // Subscribe to route params - using takeUntilDestroyed for automatic cleanup
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((pm) => {
      const nsId = pm.get('namespaceId');
      const locId = pm.get('locationId');
      const scope = this.scopeKey(nsId, locId);
      if (scope !== this.navigationScope) {
        this.clearSystemFilters();
        this.roomSearch.set('');
        this.roomFilters.set({ ...DEFAULT_ROOM_FILTERS });
        this.navigationScope = scope;
        clearTimeout(this.navigationTimer);
        this.navigationLoading.set(true);
        this.minimumHoldElapsed.set(false);
        this.settledRoomId.set(null);
        this.selectedNode.set(null);
        this.navigationTimer = setTimeout(() => this.minimumHoldElapsed.set(true), 500);
      }
      this.namespaceId.set(nsId);
      this.locationId.set(locId);
      // The selected tenant is persisted by TenantService from the route.
    });

    const clock = setInterval(() => this.now.set(Date.now()), 30_000);
    this.destroyRef.onDestroy(() => {
      clearInterval(clock);
      clearTimeout(this.navigationTimer);
    });

    // effect to refresh devices on location/namespace change
    effect(() => {
      const ns = this.namespaceId();
      if (ns) {
        // Use existing method to ensure devices loaded once; could extend to scoped fetch later
        this.api.refreshDevicesOnce(true); // force refresh on namespace change
      }
    });

    // effect to load linked devices when namespace/location changes
    effect((onCleanup) => {
      const ns = this.namespaceId();
      const loc = this.locationId();
      if (ns) {
        this.loadLinkedDevices(ns, loc || undefined);
        const sub = this.subscribeToLinkedDeviceUpdates(ns, loc || undefined);
        // Clean up subscription when effect re-runs or component destroys
        onCleanup(() => {
          this.linkedDevicesRequest++;
          if (sub) sub.unsubscribe();
        });
      } else {
        this.filteredLinkedDevices.set([]);
      }
    });

    // Room feedback for the tenant: reload on a tenant change, and (throttled) when one of its rooms gets new feedback.
    effect((onCleanup) => {
      const ns = this.namespaceId();
      if (!ns) {
        this.roomFeedback.set(null);
        return;
      }
      void this.loadRoomFeedback(ns);
      const sub = this.api.feedbackEvents$
        ?.pipe(
          filter((event) => !!event.locationId && !!this.locations()?.some((location) => location.id === event.locationId && location.appNamespaceId === ns)),
          throttleTime(10_000, asyncScheduler, { leading: true, trailing: true })
        )
        .subscribe(() => void this.loadRoomFeedback(ns));
      onCleanup(() => {
        this.roomFeedbackRequest++;
        sub?.unsubscribe();
      });
    });

    // Initialize context menu items
    this.deviceContextMenuItems = [
      {
        label: 'Open Device',
        icon: 'pi pi-external-link',
        command: () => {
          const target = this.contextMenuTarget();
          if (target) this.router.navigate(['/device', target.id]);
        }
      },
      {
        label: 'Rename Device',
        icon: 'pi pi-pencil',
        command: () => {
          const target = this.contextMenuTarget();
          if (!target) return;
          this.renameTarget.set(target);
          // Seed with the current override only. Leaving it blank when the device's own name
          // is in use makes "save empty" mean "keep following the device".
          this.renameValue.set(target.nameSetByUserUtc ? (target.name ?? '') : '');
          this.renameError.set(null);
          this.showRenameDialog.set(true);
        }
      },
      {
        label: 'Move Device',
        icon: 'pi pi-arrow-right',
        command: () => {
          const target = this.contextMenuTarget();
          if (!target) return;
          // Pre-populate with device's existing location ('' indicates root/none)
          const currentLoc: string | null | undefined = target.locationNodeId ?? target.LocationNodeId;
          // normalize empty value to '' for root option
          this.moveLocationNodeId.set(currentLoc ?? '');
          this.moveError.set(null);
          this.showMoveDialog.set(true);
        }
      },
      {
        label: 'Regenerate Code',
        icon: 'pi pi-refresh',
        command: () => {
          const target = this.contextMenuTarget();
          if (!target) return;
          if (!this.isAwaitingRegistration(target)) return;
          this.regenTarget.set(target);
          this.regenError.set(null);
          this.regenResult.set(null);
          this.showRegenerateDialog.set(true);
        }
      },
      {
        label: 'Delete Device',
        icon: 'pi pi-trash',
        command: () => {
          const target = this.contextMenuTarget();
          if (!target) return;
          // Only allow delete if unclaimed (firstClaimedUtc absent)
          const claimed = !!(target.isClaimed || target.IsClaimed);
          if (claimed) return;
          this.deleteDeviceTarget.set(target);
          this.deleteDeviceError.set(null);
          this.showDeleteDeviceDialog.set(true);
        }
      }
    ];

    // Initialize child device context menu items
    this.linkedDeviceContextMenuItems = [
      {
        label: 'Move Device',
        icon: 'pi pi-arrow-right',
        command: () => {
          const target = this.contextMenuTarget();
          if (!target) return;
          // Pre-populate with device's existing location
          const currentLoc: string | null | undefined = target.locationNodeId ?? target.LocationNodeId;
          this.moveLocationNodeId.set(currentLoc ?? '');
          this.moveError.set(null);
          this.showMoveDialog.set(true);
        }
      },
      {
        label: 'Remove Device',
        icon: 'pi pi-trash',
        visible: false,
        command: () => {
          const target = this.contextMenuTarget();
          if (!target?.id) return;
          this.openRemoveChildDialog(target);
        }
      }
    ];
  }

  /** Minimum gap between WS-triggered linked-device refetches — each refetch reads the
   *  namespace's entire linked_devices collection from Firestore, so bursts of check-in
   *  events must coalesce into a single request. */
  private static readonly LINKED_DEVICE_REFRESH_THROTTLE_MS = 15_000;

  private subscribeToLinkedDeviceUpdates(namespaceId: string, locationId?: string) {
    // Return subscription for manual cleanup in effect
    // Don't use takeUntilDestroyed here since this is called from inside an effect
    return this.api
      .subscribeToLinkedDeviceUpdates(namespaceId)
      .pipe(
        filter((event) => !!event),
        // leading: react to the first event immediately; trailing: one refetch at the end of
        // the window picks up anything that changed during the throttled period
        throttleTime(Dashboard.LINKED_DEVICE_REFRESH_THROTTLE_MS, asyncScheduler, { leading: true, trailing: true })
      )
      .subscribe(() => {
        this.silentRefreshLinkedDevices(namespaceId, locationId);
        // LocationDetailsComponent manages its own sync subscription for room data
      });
  }

  getLastSeenTimestamp(device: any): string | null {
    return device?.lastSeenUtc ?? device?.lastCheckInUtc ?? null;
  }

  lastCheckInAgo(device: any): string {
    const ts = this.getLastSeenTimestamp(device);
    if (!ts) return 'Never';
    // moment handles ISO strings; show relative with a short fallback if invalid
    const m = moment.utc(ts);
    if (!m.isValid()) return 'Never';
    return m.fromNow();
  }

  formatUptime(device: any): string {
    const uptimeAtCheckIn = device?.uptimeSeconds;
    if (uptimeAtCheckIn == null || uptimeAtCheckIn < 0) return '—';

    // Calculate current uptime by adding time elapsed since last check-in
    let totalSeconds = uptimeAtCheckIn;
    const lastCheckIn = device?.lastCheckInUtc;
    if (lastCheckIn) {
      const checkInMoment = moment.utc(lastCheckIn);
      if (checkInMoment.isValid()) {
        const elapsedSeconds = moment.utc().diff(checkInMoment, 'seconds');
        if (elapsedSeconds > 0) {
          totalSeconds += elapsedSeconds;
        }
      }
    }

    return moment.duration(totalSeconds, 'seconds').humanize();
  }

  formatCreatedDate(device: any): string {
    const created = device?.createdUtc;
    if (!created) return '—';
    const m = moment.utc(created);
    if (!m.isValid()) return '—';
    return m.local().format('MMM D, YYYY');
  }

  // Row click: a device still waiting for its registration code opens the regenerate dialog;
  // anything that has been claimed opens the System page.
  onDeviceRowClick(device: any) {
    if (!device) return;
    if (this.isAwaitingRegistration(device)) {
      this.regenTarget.set(device);
      this.regenError.set(null);
      this.regenResult.set(null);
      this.showRegenerateDialog.set(true);
      return;
    }
    this.router.navigate(['/device', device.id]);
  }

  confirmRegenerate() {
    if (this.regenSubmitting()) return;
    const target = this.regenTarget();
    if (!target) {
      this.showRegenerateDialog.set(false);
      return;
    }
    this.regenSubmitting.set(true);
    this.regenError.set(null);
    this.api
      .regenerateDeviceCode(target.id)
      .then((res) => {
        this.regenSubmitting.set(false);
        this.regenResult.set(res);
        // update devices list with fresh code
        const list = this.devices();
        if (list) {
          const updated = list.map((d) => (d.id === target.id ? { ...d, registrationCodeStatus: 'valid', registrationCodeExpired: false, registrationExpiresUtc: res.registrationExpiresUtc } : d));
          this.devices.set(updated as any);
        }
      })
      .catch((err) => {
        const msg = err?.error || err?.message || 'Failed to regenerate code';
        this.regenError.set(typeof msg === 'string' ? msg : 'Failed to regenerate code');
        this.regenSubmitting.set(false);
      });
  }

  closeRegenerateDialog() {
    if (!this.regenSubmitting()) this.showRegenerateDialog.set(false);
  }

  closeRenameDialog() {
    if (!this.renameSubmitting()) this.showRenameDialog.set(false);
  }

  /** The name the device itself reports, if any — shown as the alternative to an override. */
  renameSystemName(): string | null {
    const target = this.renameTarget();
    return target?.systemName ?? target?.payload?.systemName ?? null;
  }

  confirmRename() {
    if (this.renameSubmitting()) return;
    const target = this.renameTarget();
    if (!target?.id) {
      this.showRenameDialog.set(false);
      return;
    }
    // A blank value clears the override, handing the device back to the name it reports.
    const name = this.renameValue().trim() || null;
    this.renameSubmitting.set(true);
    this.renameError.set(null);
    this.api
      .renameDevice(target.id, name)
      .then(() => this.showRenameDialog.set(false))
      .catch((err) => this.renameError.set(err?.error || err?.message || 'Could not update the device name'))
      .finally(() => this.renameSubmitting.set(false));
  }

  /** Clears the override so the device's own system name is used again. */
  useSystemName() {
    this.renameValue.set('');
    this.confirmRename();
  }

  deleteExpiredDevice() {
    if (this.regenDeleteSubmitting()) return;
    const target = this.regenTarget();
    if (!target) {
      this.showRegenerateDialog.set(false);
      return;
    }
    this.regenDeleteSubmitting.set(true);
    this.regenDeleteError.set(null);
    this.api
      .deleteDevice(target.id)
      .then(() => {
        // remove from local list (WS event will also remove but we do optimistic update)
        const list = this.devices();
        if (list) this.devices.set(list.filter((d) => d.id !== target.id) as any);
        this.regenDeleteSubmitting.set(false);
        this.showRegenerateDialog.set(false);
      })
      .catch((err) => {
        const msg = err?.error || err?.message || 'Failed to delete device';
        this.regenDeleteError.set(typeof msg === 'string' ? msg : 'Failed to delete device');
        this.regenDeleteSubmitting.set(false);
      });
  }

  copy(text: string | null | undefined) {
    if (text)
      try {
        navigator.clipboard?.writeText(text);
      } catch {}
  }

  // Right-click handler to set target for context menu
  onDeviceRowContext(event: MouseEvent, device: any, menu: any) {
    event.preventDefault();
    this.contextMenuTarget.set(device);

    // Check if this is a child device
    const isLinkedDevice = !!(device.parentDeviceId || device.ParentDeviceId);
    if (isLinkedDevice) {
      const items = this.linkedDeviceContextMenuItems.map((mi) => ({
        ...mi,
        visible: mi.label === 'Remove Device' ? !!device.missing : true
      }));
      (menu as any).model = items;
      menu.show(event);
      return;
    }

    // Filter items based on device state (clone to avoid mutating original array reference)
    const items = this.deviceContextMenuItems.filter((mi) => {
      // If the device is bound to a namespace, check current user's namespace role for permissioned actions
      const nsId = device.appNamespaceId ?? device.AppNamespaceId ?? null;
      const canRead = this.api.hasNamespaceRole(nsId, [0, 1, 2]);
      const canWrite = this.api.hasNamespaceRole(nsId, [1, 2]);
      const canAdmin = this.api.hasNamespaceRole(nsId, [2]);
      if (mi.label === 'Regenerate Code') {
        // Regenerate requires Writer/Admin when namespace-bound; also device must be pending/expired
        const allowedState = this.isAwaitingRegistration(device);
        return allowedState && (nsId ? canWrite || canAdmin : true);
      }
      if (mi.label === 'Delete Device') {
        const claimed = !!(device.isClaimed || device.IsClaimed);
        if (claimed) return false; // only unclaimed
        return nsId ? canWrite || canAdmin : true;
      }
      if (mi.label === 'Move Device' || mi.label === 'Rename Device') {
        // Moving and renaming require Writer/Admin if device is namespace-bound
        return nsId ? canWrite || canAdmin : true;
      }
      if (mi.label === 'Open Device') {
        // Opening/Viewing requires at least Reader
        return nsId ? canRead || canWrite || canAdmin : true;
      }
      return true;
    });
    (menu as any).model = items; // assign dynamic model
    menu.show(event);
  }

  // Perform move
  confirmMoveDevice() {
    const target = this.contextMenuTarget();
    const newLoc = this.moveLocationNodeId();
    if (!target || !newLoc) {
      this.showMoveDialog.set(false);
      return;
    }
    this.moveSubmitting.set(true);
    this.moveError.set(null);

    // Determine if this is a child device (has parentDeviceId) or a regular device
    const isLinkedDevice = !!(target.parentDeviceId || target.ParentDeviceId);

    const movePromise = isLinkedDevice ? this.api.moveLinkedDevice(target.id, newLoc) : this.api.updateDeviceLocation(target.id, newLoc);

    movePromise
      .then((res) => {
        this.moveSubmitting.set(false);
        this.showMoveDialog.set(false);

        // Reload the appropriate list after successful move
        if (isLinkedDevice) {
          // Reload linked devices list
          const ns = this.namespaceId();
          const loc = this.locationId();
          if (ns) {
            this.loadLinkedDevices(ns, loc || undefined);
          }
        } else {
          // For regular devices, refresh the devices list
          this.api.refreshDevicesOnce(true);
        }
      })
      .catch((err) => {
        const msg = err?.error || err?.message || 'Failed to move device';
        this.moveError.set(typeof msg === 'string' ? msg : 'Failed to move device');
        this.moveSubmitting.set(false);
      });
  }

  // Simple location selection: choose currently selected tree node (or none) as new location
  useSelectedTreeLocation() {
    const node = this.selectedNode();
    if (!node) {
      this.moveLocationNodeId.set(null);
      return;
    }
    this.moveLocationNodeId.set(node.key || null);
  }

  confirmDeleteUnclaimedDevice() {
    if (this.deleteDeviceSubmitting()) return;
    const target = this.deleteDeviceTarget();
    if (!target) {
      this.showDeleteDeviceDialog.set(false);
      return;
    }
    this.deleteDeviceSubmitting.set(true);
    this.deleteDeviceError.set(null);
    this.api
      .deleteDevice(target.id)
      .then(() => {
        const list = this.devices();
        if (list) this.devices.set(list.filter((d) => d.id !== target.id) as any);
        this.deleteDeviceSubmitting.set(false);
        this.showDeleteDeviceDialog.set(false);
      })
      .catch((err) => {
        const msg = err?.error || err?.message || 'Failed to delete device';
        this.deleteDeviceError.set(typeof msg === 'string' ? msg : 'Failed to delete device');
        this.deleteDeviceSubmitting.set(false);
      });
  }

  async loadLinkedDevices(namespaceId: string, locationId?: string) {
    const request = ++this.linkedDevicesRequest;
    this.linkedDevicesLoading.set(true);
    this.linkedDevicesError.set(null);
    this.loadedLinkedScope.set(null);
    try {
      const children = await this.api.getAllLinkedDevices(namespaceId, locationId);
      if (request !== this.linkedDevicesRequest) return;
      this.filteredLinkedDevices.set(children || []);
      this.loadedLinkedScope.set(this.scopeKey(namespaceId, locationId));
    } catch (err) {
      if (request !== this.linkedDevicesRequest) return;
      console.error('Failed to load linked devices', err);
      this.linkedDevicesError.set(err instanceof Error ? err.message : 'Failed to load devices');
      this.loadedLinkedScope.set(this.scopeKey(namespaceId, locationId));
      this.filteredLinkedDevices.set([]);
    } finally {
      if (request === this.linkedDevicesRequest) this.linkedDevicesLoading.set(false);
    }
  }

  private async silentRefreshLinkedDevices(namespaceId: string, locationId?: string) {
    const scope = this.scopeKey(namespaceId, locationId);
    if (this.scopeKey(this.namespaceId(), this.locationId()) !== scope) return;
    const request = ++this.linkedDevicesRequest;
    try {
      const fresh = await this.api.getAllLinkedDevices(namespaceId, locationId);
      if (request !== this.linkedDevicesRequest || this.scopeKey(this.namespaceId(), this.locationId()) !== scope) return;
      this.linkedDevicesError.set(null);
      this.loadedLinkedScope.set(scope);
      const current = this.filteredLinkedDevices();
      const currentMap = new Map(current.map((d: any) => [d.id, d]));
      const freshMap = new Map((fresh || []).map((d: any) => [d.id, d]));
      const hasStructuralChange = current.length !== (fresh?.length ?? 0) || (fresh || []).some((d: any) => !currentMap.has(d.id)) || current.some((d: any) => !freshMap.has(d.id));
      if (hasStructuralChange) {
        this.filteredLinkedDevices.set(fresh || []);
      } else {
        let anyChange = false;
        const merged = current.map((existing: any) => {
          const f = freshMap.get(existing.id);
          if (!f) return existing;
          if (
            existing.online !== f.online ||
            existing.missing !== f.missing ||
            existing.lastSeenUtc !== f.lastSeenUtc ||
            existing.state?.powerStatus !== f.state?.powerStatus ||
            existing.state?.currentInput !== f.state?.currentInput ||
            existing.state?.inStandby !== f.state?.inStandby
          ) {
            anyChange = true;
            return { ...existing, ...f };
          }
          return existing;
        });
        if (anyChange) this.filteredLinkedDevices.set(merged);
      }
    } catch (err) {
      if (request !== this.linkedDevicesRequest || this.scopeKey(this.namespaceId(), this.locationId()) !== scope) return;
      console.error('Linked-device refresh failed (dashboard)', err);
      this.linkedDevicesError.set(err instanceof Error ? err.message : 'Failed to refresh devices');
      this.loadedLinkedScope.set(scope);
    } finally {
      if (request === this.linkedDevicesRequest) this.linkedDevicesLoading.set(false);
    }
  }

  formatLinkedLastSeen(device: any): string {
    if (device?.missing) return 'Missing';
    const ts = this.getLastSeenTimestamp(device);
    if (!ts) return 'Never seen';
    const m = moment.utc(ts);
    if (!m.isValid()) return 'Never seen';
    return device?.online ? 'Online now' : m.fromNow();
  }

  /** What the portal calls the processor a linked device hangs off. */
  readonly controlSystemLabel = CONTROL_SYSTEM_LABEL;

  /** Template helper — see linkedDeviceDisplayName for the precedence. */
  linkedDisplayName = linkedDeviceDisplayName;

  getParentDeviceName(parentDeviceId: string): string {
    const device = this.devices()?.find((d: any) => d.id === parentDeviceId);
    // Resolve through the shared precedence rather than reading `name`: a self-registered
    // control system carries a blank name until its first check-in reports a system name.
    return controlSystemDisplayName(device ? deviceDisplayName(device) : null, parentDeviceId);
  }

  getLinkedDeviceLocationName(locationId: string | undefined): string {
    const location = locationId ? this.locations()?.find((l: any) => l.id === locationId) : null;
    return locationRefName(location?.name, locationId, location?.type);
  }

  // Remove child device dialog
  showRemoveChildDialog = signal(false);
  removeChildTarget = signal<any>(null);
  removeLinkedSubmitting = signal(false);
  removeChildError = signal<string | null>(null);

  openRemoveChildDialog(device: any) {
    this.removeChildTarget.set(device);
    this.removeChildError.set(null);
    this.showRemoveChildDialog.set(true);
  }

  async confirmRemoveLinkedDevice() {
    const device = this.removeChildTarget();
    if (!device?.id) return;

    this.removeLinkedSubmitting.set(true);
    this.removeChildError.set(null);

    try {
      await this.api.deleteLinkedDevice(device.id);
      this.filteredLinkedDevices.set(this.filteredLinkedDevices().filter((c) => c.id !== device.id));
      this.showRemoveChildDialog.set(false);
      this.removeChildTarget.set(null);
    } catch (err: any) {
      console.error('Failed to remove child device', err);
      this.removeChildError.set(err?.error?.detail || err?.message || 'Failed to remove child device');
    } finally {
      this.removeLinkedSubmitting.set(false);
    }
  }
}
